import axios from "axios";
const STATIC_URL = import.meta.env.VITE_STATIC_URL;
const API = import.meta.env.VITE_API;
import sha1 from "js-sha1";
import { computed, ref } from "vue";

const IS_TEST_ENV = import.meta.env.MODE === "test";

const FORCED_CLIENT = import.meta.env.VITE_FORCE_CLIENT;
/**
 * key of object is part of the hostname company will use to access console (ex: console.sontay.com) AND the name of the file (logo-xxx, logofull-xxx, favicon-xxx)
 * value of object is the friendly name that will be used as title
 */
export const CLIENTS = {
	advizeo: "Advizeo",
	egain: "[e]gain",
	sontay: "Sontay",
	siemens: "Siemens",
	wattsense: "Wattsense",
	theiotsolutions: "The Iot Solutions"
};

const DEFAULT_PRODUCT_NAMES = {
	generic: "Box",
	genericPlural: "Boxes",
	box: "Tower",
	boxes: "Towers",
	hub: "Bridge",
	hubs: "Bridges"
};
const SIEMENS_PRODUCT_NAMES = {
	generic: "Box",
	genericPlural: "Boxes",
	box: "Connect Box",
	boxes: "Connect Boxes",
	hub: "Connect Box On Premise",
	hubs: "Connect Boxes On Premise"
};

type ConsoleConfig = {
	domainName: string;
	name: string;
	shortName: string;
	supportContact: string | null;
	documentationUrl: string | null;
	supportLink: string | null;
	helpCenterLink: string | null;
	chatLink: string | null;
	enableSupport: boolean | null;
};
const consoleConfig = ref<ConsoleConfig>();

export class ClientName {
	clientName = "";
	consoleTitle = null;
	stringsLoaded = false;
	productNames = DEFAULT_PRODUCT_NAMES;
	locationHash = null;
	window = globalThis.window;

	constructor(window = globalThis.window) {
		this.clientName = this.getClientName();
		this.locationHash = this.getLocationHash();
		this.window = window;
	}

	get forcedClient() {
		if (!IS_TEST_ENV && Object.keys(CLIENTS).includes(FORCED_CLIENT)) {
			return FORCED_CLIENT;
		}
		return "";
	}

	getClientName() {
		// needed for testing different clients
		if (this.clientName && !IS_TEST_ENV) {
			return this.clientName;
		}

		// check if user forced a specific client
		if (this.forcedClient) {
			return this.forcedClient;
		}

		const hostname = this.window.location.hostname;
		const clients = Object.keys(CLIENTS);
		for (const client of clients) {
			if (hostname.includes(client)) {
				return client;
			}
		}

		return "wattsense";
	}

	async getConsoleStrings() {
		if (this.stringsLoaded) {
			return this.productNames;
		}
		const defaultNames =
			this.clientName === "siemens"
				? SIEMENS_PRODUCT_NAMES
				: DEFAULT_PRODUCT_NAMES;

		return await axios
			.get(`${API}/console`, { params: { showSpinner: false } })
			.then((res) => {
				this.productNames = { ...defaultNames, ...res?.data };
				consoleConfig.value = res?.data;
				this.stringsLoaded = true;
				this.consoleTitle =
					this.productNames.name || this.humanClientName;
				return this.productNames;
			})
			.catch(() => {
				this.consoleTitle = this.humanClientName;
				this.productNames = defaultNames;
				this.stringsLoaded = true;
				return this.productNames;
			});
	}

	async getConsoleTitle() {
		if (this.consoleTitle) {
			return this.consoleTitle;
		}
		await this.getConsoleStrings();
		return this.consoleTitle;
	}

	getLocationHash() {
		// needed for testing different window.location.hostname
		if (this.locationHash && !IS_TEST_ENV) {
			return this.locationHash;
		}
		return sha1(this.window.location.hostname).toUpperCase();
	}

	get humanClientName() {
		const client = this.getClientName();
		return CLIENTS[client] || "Wattsense";
	}

	async setFavicon() {
		const newFavicon = document.createElement("link");
		newFavicon.id = "favicon";
		newFavicon.type = "image/x-icon";
		newFavicon.rel = "icon";
		newFavicon.href = await this.getFaviconUrl();

		const oldIcon = document.querySelector("#favicon");
		if (oldIcon) {
			document.head.removeChild(oldIcon);
		}
		document.head.append(newFavicon);
	}

	async setTitle() {
		document.querySelectorAll("title")[0].text =
			await this.getConsoleTitle();
	}

	getFaviconUrl() {
		if (this.forcedClient) {
			return (
				import.meta.env.BASE_URL +
				`favicon-${this.forcedClient}.ico?=${Math.random()}`
			);
		}
		// Math.random() is to avoid browser displaying cached favicon
		// return `${STATIC_URL}/${this.getLocationHash()}/favicon?=${Math.random()}`;
		return axios
			.get(`${STATIC_URL}/${this.getLocationHash()}/favicon`, {
				params: { showSpinner: false }
			})
			.then(() => {
				return `${STATIC_URL}/${this.getLocationHash()}/favicon?=${Math.random()}`;
			})
			.catch(() => {
				return (
					import.meta.env.BASE_URL +
					`favicon-${this.getClientName()}.ico?=${Math.random()}`
				);
			});
	}

	getLogoUrls() {
		if (this.forcedClient) {
			const small = new URL(
				`../../assets/imgs/logos/logo-${this.forcedClient}.svg`,
				import.meta.url
			).href;
			const big = new URL(
				`../../assets/imgs/logos/logofull-${this.forcedClient}.svg`,
				import.meta.url
			).href;
			return {
				small,
				big,
				defaultSmall: small,
				defaultBig: big
			};
		}
		return {
			small: `${STATIC_URL}/${this.getLocationHash()}/logo_collapsed`,
			big: `${STATIC_URL}/${this.getLocationHash()}/logo_full`,
			defaultSmall: new URL(
				`../../assets/imgs/logos/logo-${this.getClientName()}.svg`,
				import.meta.url
			).href,
			defaultBig: new URL(
				`../../assets/imgs/logos/logofull-${this.getClientName()}.svg`,
				import.meta.url
			).href
		};
	}
}

const client = new ClientName();

export const useClientName = () => ({
	clientName: client.getClientName(),
	humanClientName: client.humanClientName,
	productNames: client.productNames,
	getLogoUrls: client.getLogoUrls.bind(client),
	setClientTitleAndFavicon: async () => {
		await client.setFavicon();
		await client.getConsoleStrings();
		await client.setTitle();
	}
});

export function useConsoleConfig() {
	const enableSupport = computed<boolean>(() => {
		if (import.meta.env.DEV || consoleConfig.value === undefined) {
			return client.clientName !== "theiotsolutions";
		}
		return (
			!!consoleConfig.value?.enableSupport ||
			(!!consoleConfig.value?.helpCenterLink &&
				!!consoleConfig.value?.documentationUrl)
		);
	});

	return {
		consoleConfig,
		enableSupport
	};
}

export default {
	install(app) {
		app.config.globalProperties.$clientName = client.getClientName();
		app.config.globalProperties.$staticImgUrl = `${STATIC_URL}/${client.getLocationHash()}`;
		app.config.globalProperties.$setClientTitleAndFavicon = async () => {
			await client.setFavicon();
			await client.getConsoleStrings();
			await client.setTitle();
		};
	}
};
